/**
 * Sample Data Configuration
 * This file contains configuration for sample data generation.
 * 
 * SECURITY NOTE: Sample data should NEVER be used in production environments.
 * This is enforced by environment checks throughout the sample data system.
 */

import type { Club, Organization } from '@/lib/types';

// Security check: Ensure sample data is never used in actual production deployments
// Only block production deployments - staging is allowed to use sample data for demos
const isProductionDeployment = process.env.NEXT_PUBLIC_ENVIRONMENT === 'production' || 
                               process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID === 'courtly-production';

// Check if Firebase credentials are placeholder values (indicating incomplete setup)
const hasPlaceholderCredentials = process.env.FIREBASE_PRIVATE_KEY?.includes('PLACEHOLDER') ||
                                  process.env.FIREBASE_PRIVATE_KEY?.includes('YOUR_') ||
                                  !process.env.FIREBASE_PRIVATE_KEY;

// Allow sample data in production builds when credentials are incomplete/placeholder
// This allows the build process to complete even with incomplete Firebase setup
if (isProductionDeployment && 
    process.env.NEXT_PUBLIC_USE_SAMPLE_DATA === 'true' && 
    !hasPlaceholderCredentials) {
  throw new Error(
    'SECURITY ERROR: Sample data is enabled in production environment. ' +
    'Set NEXT_PUBLIC_USE_SAMPLE_DATA=false for production deployments.'
  );
}

// Use fixed numeric IDs for consistency across the application
const DEFAULT_ORG_ID = '8392746510';
const DEFAULT_CLUB_ID = '5847293610';

export const DEFAULT_ORGANIZATION: Organization = {
  id: DEFAULT_ORG_ID,
  name: 'Courtly Sports Inc.',
  status: 'Active',
  currencyCode: 'USD',
  tenantId: DEFAULT_ORG_ID, // Organization IS the tenant root
};

export const DEFAULT_CLUB: Club = {
  id: DEFAULT_CLUB_ID,
  name: 'Topspin Tennis Club',
  status: 'Active',
  sportsOffered: ['Tennis', 'Padel', 'Foot5'],
  currencyCode: 'CHF',
  calendarStartTime: '08:00',
  calendarEndTime: '23:00',
  organizationId: DEFAULT_ORG_ID,
  tenantId: DEFAULT_ORG_ID,
};

// Sample data generation settings
export const SAMPLE_DATA_CONFIG = {
  defaultOrganizationId: DEFAULT_ORG_ID,
  defaultClubId: DEFAULT_CLUB_ID,
  // Add more configuration as needed
  numberOfUsersToGenerate: 10,
  numberOfSpacesToGenerate: 5,
  numberOfBookingsToGenerate: 20,
}; 
